import {
  U32_MAX,
  CalculatorUserLayerV4ValidationError,
  MAX_CALCULATOR_OVERLAY_PRESET_COMMENT_CHARS_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_JSON_SAFE_INTEGER_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_NESTED_LIST_ITEMS_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_OPERATIONS_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_PAYLOAD_BYTES_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_STRING_CHARS_V4,
  ValidationContext,
  deepFreeze,
  fieldPath,
  parseLayerProvenance,
} from "./calculator-user-layer-v4-values.js";
import {
  canonicalizeCalculatorUserLayerOperationV4,
  parseCalculatorUserLayerOperationIdV4,
  parseCalculatorUserLayerOperationV4,
} from "./calculator-user-layer-v4-operations.js";

export const CALCULATOR_USER_LAYER_DOCUMENT_VERSION = 4;
export {
  CalculatorUserLayerV4ValidationError,
  MAX_CALCULATOR_OVERLAY_PRESET_COMMENT_CHARS_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_JSON_SAFE_INTEGER_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_NESTED_LIST_ITEMS_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_OPERATIONS_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_PAYLOAD_BYTES_V4,
  MAX_CALCULATOR_OVERLAY_PRESET_STRING_CHARS_V4,
  parseCalculatorUserLayerOperationIdV4,
  parseCalculatorUserLayerOperationV4,
};

const JSON_TEXT_ENCODER = new TextEncoder();

function validateStorageBounds(value, path, context, fieldName = null) {
  if (Array.isArray(value)) {
    const maximum = fieldName === "operations"
      ? MAX_CALCULATOR_OVERLAY_PRESET_OPERATIONS_V4
      : MAX_CALCULATOR_OVERLAY_PRESET_NESTED_LIST_ITEMS_V4;
    if (value.length > maximum) {
      context.issue(path, "invalidLength", `expected at most ${maximum} items`);
      return;
    }
    value.forEach((child, index) => validateStorageBounds(
      child,
      `${path}[${index}]`,
      context,
    ));
    return;
  }
  if (!value || typeof value !== "object") {
    if (typeof value === "string") {
      if (!value.isWellFormed()) {
        context.issue(path, "invalidUnicode", "must contain valid Unicode scalar values");
      }
      const maximum = fieldName === "comment"
        ? MAX_CALCULATOR_OVERLAY_PRESET_COMMENT_CHARS_V4
        : MAX_CALCULATOR_OVERLAY_PRESET_STRING_CHARS_V4;
      if ([...value].length > maximum) {
        context.issue(path, "invalidLength", `expected at most ${maximum} characters`);
      }
    }
    return;
  }
  for (const [name, child] of Object.entries(value)) {
    validateStorageBounds(child, fieldPath(path, name), context, name);
  }
}

function parseDocument(value, context) {
  const object = context.exactObject(value, "$", ["documentVersion", "userLayer"]);
  if (object.documentVersion !== CALCULATOR_USER_LAYER_DOCUMENT_VERSION) {
    context.issue(
      "$.documentVersion",
      "unsupportedVersion",
      `expected exactly ${CALCULATOR_USER_LAYER_DOCUMENT_VERSION}`,
    );
  }
  const layerPath = "$.userLayer";
  const layer = context.exactObject(object.userLayer, layerPath, [
    "layerId",
    "name",
    "order",
    "evidenceClass",
    "provenance",
    "operations",
  ]);
  if (layer.evidenceClass !== "user") {
    context.issue(
      fieldPath(layerPath, "evidenceClass"),
      "unsupportedValue",
      "a user overlay document must use evidenceClass user",
    );
  }
  const operationsPath = fieldPath(layerPath, "operations");
  const operations = context
    .array(layer.operations, operationsPath, {
      maxItems: MAX_CALCULATOR_OVERLAY_PRESET_OPERATIONS_V4,
    })
    .map((operation, index) => canonicalizeCalculatorUserLayerOperationV4(
      operation,
      `${operationsPath}[${index}]`,
      context,
    ));

  const operationIds = new Map();
  const factIds = new Map();
  operations.forEach((operation, index) => {
    if (operation.operationId) {
      if (operationIds.has(operation.operationId)) {
        context.issue(
          `${operationsPath}[${index}].operationId`,
          "duplicateOperationId",
          `duplicates operation ${operationIds.get(operation.operationId)}`,
        );
      } else {
        operationIds.set(operation.operationId, index);
      }
    }
    if (operation.factId) {
      if (factIds.has(operation.factId)) {
        context.issue(
          `${operationsPath}[${index}].factId`,
          "duplicateFactId",
          `duplicates fact ${factIds.get(operation.factId)}`,
        );
      } else {
        factIds.set(operation.factId, index);
      }
    }
  });

  return {
    documentVersion: CALCULATOR_USER_LAYER_DOCUMENT_VERSION,
    userLayer: {
      layerId: context.string(layer.layerId, fieldPath(layerPath, "layerId"), {
        opaqueId: true,
      }),
      name: context.string(layer.name, fieldPath(layerPath, "name")),
      order: context.integer(layer.order, fieldPath(layerPath, "order"), 0, U32_MAX),
      evidenceClass: "user",
      provenance: parseLayerProvenance(
        layer.provenance,
        fieldPath(layerPath, "provenance"),
        context,
      ),
      operations,
    },
  };
}

export function parseCalculatorUserOverlayDocumentV4(value) {
  const context = new ValidationContext();
  const document = parseDocument(value, context);
  validateStorageBounds(document, "$", context);
  context.finish();
  if (
    JSON_TEXT_ENCODER.encode(JSON.stringify(document)).byteLength
    > MAX_CALCULATOR_OVERLAY_PRESET_PAYLOAD_BYTES_V4
  ) {
    throw new CalculatorUserLayerV4ValidationError([{
      path: "$",
      code: "payloadTooLarge",
      message: `compact payload must contain at most ${MAX_CALCULATOR_OVERLAY_PRESET_PAYLOAD_BYTES_V4} bytes`,
    }]);
  }
  return deepFreeze(document);
}

export function importCalculatorUserOverlayDocumentV4(text) {
  if (typeof text !== "string" || !text.trim()) {
    throw new CalculatorUserLayerV4ValidationError([
      { path: "$", code: "invalidJson", message: "expected non-empty JSON text" },
    ]);
  }
  let value;
  try {
    value = JSON.parse(text);
  } catch (error) {
    throw new CalculatorUserLayerV4ValidationError([
      {
        path: "$",
        code: "invalidJson",
        message: `invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
      },
    ]);
  }
  return parseCalculatorUserOverlayDocumentV4(value);
}

export function exportCalculatorUserOverlayDocumentV4(document, space = 2) {
  const normalized = parseCalculatorUserOverlayDocumentV4(document);
  return JSON.stringify(normalized, null, space);
}

export function createCalculatorUserOverlayDocumentV4(userLayer) {
  return parseCalculatorUserOverlayDocumentV4({
    documentVersion: CALCULATOR_USER_LAYER_DOCUMENT_VERSION,
    userLayer,
  });
}
